<template>
  <table
    id="time-and-activities-table"
    :class="`table-hover reports-table time-and-activities-table table group-by-${walker.main_type}`"
  >
    <tbody class="thead">
      <tr>
        <template v-if="memberInfoColumnPosition === 0">
          <th v-if="tableShowMemberInfo">
            <ReportMemberFieldsColumnHeader type="info" />
          </th>
          <th v-if="tableShowMemberEmployment">
            <ReportMemberFieldsColumnHeader type="employment" />
          </th>
          <th v-if="tableShowTeams">Teams</th>
        </template>
        <template v-if="walker.sub_show">
          <th>{{ walker.sub_label }}</th>
        </template>
        <template v-if="memberInfoColumnPosition === 1">
          <th v-if="tableShowMemberInfo">
            <ReportMemberFieldsColumnHeader type="info" />
          </th>
          <th v-if="tableShowMemberEmployment">
            <ReportMemberFieldsColumnHeader type="employment" />
          </th>
          <th v-if="tableShowTeams">Teams</th>
        </template>
        <template v-if="walker.third_show">
          <th>{{ walker.third_label }}</th>
        </template>
        <template v-if="memberInfoColumnPosition === 2">
          <th v-if="tableShowMemberInfo">
            <ReportMemberFieldsColumnHeader type="info" />
          </th>
          <th v-if="tableShowMemberEmployment">
            <ReportMemberFieldsColumnHeader type="employment" />
          </th>
          <th v-if="tableShowTeams">Teams</th>
        </template>
        <template v-if="walker.fourth_show">
          <th>{{ walker.fourth_label }}</th>
        </template>
        <template v-if="memberInfoColumnPosition === 3">
          <th v-if="tableShowMemberInfo">
            <ReportMemberFieldsColumnHeader type="info" />
          </th>
          <th v-if="tableShowMemberEmployment">
            <ReportMemberFieldsColumnHeader type="employment" />
          </th>
          <th v-if="tableShowTeams">Teams</th>
        </template>
        <th v-if="tableShowClient">Client</th>
        <th v-if="tableShowTasks">
          {{ tableTaskLabel }}
        </th>
        <th v-if="hasTimeBreakdown">
          Regular hours
          <ReportTableTooltipIcon
            v-if="workBreaksEnabled"
            message="Regular hours includes work break time. Overtime calculations exclude work break time."
          />
        </th>
        <th v-if="tableShowOvertime">Overtime</th>
        <th v-if="tableShowTimeOffHoliday">Time off/Holiday</th>
        <th v-if="hasTimeBreakdown">Total hours</th>
        <th v-else>Time</th>
        <th v-if="workBreaksEnabled && tableShowBreakTime">Break time</th>
        <th v-if="tableShowActivity">Activity</th>
        <th v-if="tableShowManualTime">Manual hours</th>
        <th v-if="tableShowIdle">Idle (%)</th>
        <th v-if="tableShowIdleTime">Idle (hr)</th>
        <th v-if="tableShowSpentTotal">
          Total {{ spentLabel }}
          <ReportTableTooltipIcon
            v-if="!displaySpentValue && tableIsTeam"
            :message="ownershipSpentTooltip"
          />
          <ReportTableTooltipIcon
            :message="mismatchTooltip"
            placement="auto top"
            type="warning"
          />
        </th>
        <th v-if="tableShowSpentRegular">
          Regular {{ spentLabel }}
          <ReportTableTooltipIcon
            v-if="!displaySpentValue"
            :message="ownershipSpentTooltip"
          />
        </th>
        <th v-if="tableShowSpentOvertime">
          Overtime {{ spentLabel }}
          <ReportTableTooltipIcon
            v-if="!displaySpentValue"
            :message="ownershipSpentTooltip"
          />
        </th>
        <th v-if="tableShowSpentTimeOff">
          PTO {{ spentLabel }}
          <ReportTableTooltipIcon
            v-if="!displaySpentValue"
            :message="ownershipSpentTooltip"
          />
        </th>
        <th v-if="tableShowSpentHoliday">
          Holiday {{ spentLabel }}
          <ReportTableTooltipIcon
            v-if="!displaySpentValue"
            :message="ownershipSpentTooltip"
          />
        </th>
        <th
          v-if="tableShowBilledAmount"
          data-tname="billed_amount"
        >
          Billed amount
          <ReportTableTooltipIcon :message="ownershipBillableTooltip" />
          <ReportTableTooltipIcon
            :message="mismatchTooltip"
            placement="auto top"
            type="warning"
          />
        </th>
        <th
          v-if="tableShowBillableTime"
          data-tname="billable_time"
        >
          Billable time
          <ReportTableTooltipIcon :message="ownershipBillableTooltip" />
        </th>
        <th
          v-if="tableShowNonBillableTime"
          data-tname="non_billable_time"
        >
          Non-billable time
          <ReportTableTooltipIcon :message="ownershipBillableTooltip" />
        </th>
        <th v-if="tableShowNotes">Notes</th>
      </tr>
    </tbody>
    <ReportVirtualTableBody
      v-slot="{ row: section }"
      :rows="sections"
    >
      <tr class="grouping-header">
        <th colspan="100%">
          <template v-if="walker.main_type === 'date'">
            {{ format_date_label(section.main) }}
          </template>
          <template v-else-if="walker.main_type === 'project' || walker.main_type === 'client'">
            <template v-if="walker.main_type === 'client' && section.main_id == null">
              <span class="text-lighter">No client</span>
            </template>
            <template v-else-if="walker.main_type === 'client' && section.main_id === 0">
              <span class="text-lighter">N/A</span>
            </template>
            <template v-else-if="walker.main_type === 'project' && section.main_id == null">
              <span class="text-lighter">No project</span>
            </template>
            <template v-else>
              <div
                v-tooltip
                :class="projectClass(section.rows[0].record.item_type, walker.main_type, null, section.main_id, section.main)"
                :title="projectTitle(section.rows[0].record.item_type, walker.main_type, null, section.main_id, section.main)"
                @click="onProjectClick(section.main_id, section.rows[0].record.item_type, walker.main_type, null, section.main)"
              >
                <ReportTableAvatarIconInitial
                  icon-class="report-avatar-icon"
                  :icons="avatarIconsMap"
                  :icon-key="section.rows[0].record.item_type"
                  :text="section.main"
                />
                <div>{{ section.main }}</div>
              </div>
            </template>
          </template>
          <template v-else-if="walker.main_type === 'user'">
            <MemberPaymentAccountPopup
              :organization-id="organization.id"
              :user-id="section.main_id"
              :image-url="section.rows[0].record.avatar_url"
              :name="section.main"
              :show-member-profile="showMemberProfile"
              tab="settings"
              title=""
            />
          </template>
          <template v-else-if="walker.main_type === 'task'">
            <template v-if="section.main">
              <TimeAndActivitiesReportTaskPopover
                v-if="hasRemoteTaskId(section.rows[0].record)"
                :record="section.rows[0].record"
              >
                {{ section.main }}
              </TimeAndActivitiesReportTaskPopover>
              <template v-else>
                <AppPopover
                  trigger="hover"
                  placement="top"
                >
                  <template #body>
                    <span>
                      {{ section.main }}
                    </span>
                  </template>
                  <template #trigger>
                    <span>
                      {{ section.main }}
                    </span>
                  </template>
                </AppPopover>
              </template>
            </template>
            <template v-else>
              <span
                v-if="section.main_id === 0"
                class="text-lighter"
              >
                N/A
              </span>
              <span
                v-else
                class="text-lighter"
              >
                {{ groupNoTaskLabel }}
              </span>
            </template>
          </template>
          <template v-else-if="walker.main_type === 'team'">
            <template v-if="section.main_id == null">
              <span class="text-lighter">No team</span>
            </template>
            <template v-else>
              <ReportTableAvatarInitial :text="section.main" />
              {{ section.main }}
            </template>
          </template>
        </th>
      </tr>

      <tr
        class="ttotal within-virtual cursor-pointer"
        :class="{ 'is-expanded': section.show }"
        @click="toggleSection(section)"
      >
        <th>
          <span
            class="expand-rows-toggle"
            :class="{ 'is-expanded': section.show }"
          >
            <i class="hi hi-caret" />
            <span class=""> Total </span>
          </span>
        </th>
        <template v-if="memberInfoColumnPosition === 0">
          <td v-if="tableShowMemberInfo" />
          <td v-if="tableShowMemberEmployment" />
          <td v-if="tableShowTeams" />
        </template>
        <td v-if="showTotalSpacerForSlot('sub')" />
        <template v-if="memberInfoColumnPosition === 1">
          <td v-if="tableShowMemberInfo" />
          <td v-if="tableShowMemberEmployment" />
          <td v-if="tableShowTeams" />
        </template>
        <td v-if="showTotalSpacerForSlot('third')" />
        <template v-if="memberInfoColumnPosition === 2">
          <td v-if="tableShowMemberInfo" />
          <td v-if="tableShowMemberEmployment" />
          <td v-if="tableShowTeams" />
        </template>
        <td v-if="showTotalSpacerForSlot('fourth')" />
        <template v-if="memberInfoColumnPosition === 3">
          <td v-if="tableShowMemberInfo" />
          <td v-if="tableShowMemberEmployment" />
          <td v-if="tableShowTeams" />
        </template>
        <td v-if="tableShowClient" />
        <td v-if="tableShowTasks" />
        <td v-if="hasTimeBreakdown">{{ formatDuration(section.total.regular_tracked) }}</td>
        <td v-if="tableShowOvertime">{{ formatDuration(section.total.overtime) }}</td>
        <td
          v-if="tableShowTimeOffHoliday"
          data-tname="time_off_holiday_totals"
        >
          <ReportTableTooltipIcon
            v-if="section.total.pto_stop_outside > 0"
            :message="timeoffPartialTotalTooltip"
            placement="auto top"
            type="warning"
          />
          {{ formatDuration(section.total.time_off_holiday) }}
        </td>
        <td>{{ $filters.formatDuration(section.total.tracked, 'user', '-', false, true) }}</td>
        <td v-if="workBreaksEnabled && tableShowBreakTime">
          {{ formatDuration(section.total.break) }}
        </td>
        <td v-if="tableShowActivity">
          {{ $filters.percentage(section.total.overall, section.total.input_tracked) }}
        </td>
        <td v-if="tableShowManualTime">
          {{ $filters.formatDuration(section.total.manual, 'user', '-', false, true) }}
        </td>
        <td v-if="tableShowIdle">
          {{ $filters.percentage(section.total.idle, section.total.tracked) }}
        </td>
        <td v-if="tableShowIdleTime">
          {{ formatDuration(section.total.idle) }}
        </td>
        <template v-if="tableShowSpentTotal">
          <td>
            <ReportTableTooltipIcon
              v-if="displaySpentValue && showTimeoffPartialTotal(section.total)"
              data-html
              :message="sectionSpentTotalTooltip(section.total)"
              placement="auto top"
              type="warning"
            />
            {{ formatSpent(section.total.spent, '') }}
          </td>
        </template>
        <td v-if="tableShowSpentRegular">
          {{ formatSpent(section.total.spent_regular, '') }}
        </td>
        <td v-if="tableShowSpentOvertime">
          {{ formatSpent(section.total.spent_overtime, '') }}
        </td>
        <td v-if="tableShowSpentTimeOff">
          <ReportTableTooltipIcon
            v-if="displaySpentValue && showTimeoffPartialTotal(section.total)"
            :message="timeoffPartialTotalTooltip"
            placement="auto top"
            type="warning"
          />
          {{ formatSpent(section.total.spent_time_off, '') }}
        </td>
        <td v-if="tableShowSpentHoliday">
          {{ formatSpent(section.total.spent_holiday, '') }}
        </td>
        <template v-if="tableShowBilledAmount">
          <td v-if="viewPaymentInfo">
            {{ $filters.currency(section.total.billed_amount, organization.currency) }}
          </td>
          <td v-else />
        </template>
        <template v-if="tableShowBillableTime">
          <td v-if="viewPaymentInfo">
            {{ formatDuration(section.total.billable_time) }}
          </td>
          <td v-else />
        </template>
        <template v-if="tableShowNonBillableTime">
          <td v-if="viewPaymentInfo">
            {{ formatDuration(section.total.non_billable_time) }}
          </td>
          <td v-else />
        </template>
        <td
          v-if="tableShowNotes"
          class="long-text-cell"
        />
      </tr>
      <ReportVirtualTableBody
        v-if="section.show"
        :key="`table-${section.uuid}`"
        v-slot="{ row, index }"
        :rows="section.rows"
        :item-size="section.itemSize"
        nested
      >
        <tr>
          <template v-if="memberInfoColumnPosition === 0">
            <td v-if="tableShowMemberInfo">
              <template v-if="index === 0">
                <ReportMemberFields
                  type="info"
                  :data="row.record"
                  :fields="section.infoFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowMemberEmployment">
              <template v-if="index === 0">
                <ReportMemberFields
                  type="employment"
                  :data="row.record"
                  :fields="section.employmentFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowTeams">
              <ReportTeamsField
                v-if="index === 0"
                :teams="row.record.teams"
              />
            </td>
          </template>
          <template v-if="walker.sub_show">
            <td
              v-if="row.sub_show_data"
              class="row.sub_show_data"
            >
              <template v-if="walker.sub_type === 'user'">
                <MemberPaymentAccountPopup
                  :organization-id="organization.id"
                  :user-id="row.record.sub_id"
                  :image-url="row.record.avatar_url"
                  :name="row.record.sub"
                  :show-member-profile="showMemberProfile"
                  tab="settings"
                  title=""
                />
              </template>
              <template v-else-if="walker.sub_type === 'project' || walker.sub_type === 'client'">
                <template v-if="walker.sub_type === 'client' && row.record.sub_id == null">
                  <span class="text-lighter">No client</span>
                </template>
                <template v-else-if="walker.sub_type === 'client' && row.record.sub_id == 0">
                  <span class="text-lighter">N/A</span>
                </template>
                <template v-else-if="walker.sub_type === 'project' && row.record.sub_id == null">
                  <span class="text-lighter">No project</span>
                </template>
                <template v-else>
                  <div
                    v-tooltip
                    :class="projectClass(row.record.item_type, null, walker.sub_type, row.record.sub_id, row.record.sub)"
                    :title="projectTitle(row.record.item_type, null, walker.sub_type, row.record.sub_id, row.record.sub)"
                    @click="onProjectClick(row.record.sub_id, row.record.item_type, null, walker.sub_type, row.record.sub)"
                  >
                    <ReportTableAvatarIconInitial
                      icon-class="report-avatar-icon"
                      :icons="avatarIconsMap"
                      :icon-key="row.record.item_type"
                      :text="row.record.sub"
                    />
                    <div class="ellipsis">{{ row.record.sub }}</div>
                  </div>
                </template>
              </template>
              <template v-else-if="walker.sub_type === 'date'">
                {{ format_date_label(row.record.sub) }}
              </template>
              <template v-else>
                {{ row.record.sub }}
              </template>
            </td>
            <td v-else />
          </template>
          <template v-if="memberInfoColumnPosition === 1">
            <td v-if="tableShowMemberInfo">
              <template v-if="row.sub_show_data">
                <ReportMemberFields
                  type="info"
                  :data="row.record"
                  :fields="section.infoFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowMemberEmployment">
              <template v-if="row.sub_show_data">
                <ReportMemberFields
                  type="employment"
                  :data="row.record"
                  :fields="section.employmentFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowTeams">
              <ReportTeamsField
                v-if="row.sub_show_data"
                :teams="row.record.teams"
              />
            </td>
          </template>
          <template v-if="walker.third_show">
            <td
              v-if="row.third_show_data"
              class="row.third_show_data"
            >
              <template v-if="walker.third_type === 'user'">
                <MemberPaymentAccountPopup
                  :organization-id="organization.id"
                  :user-id="row.record.third_id"
                  :image-url="row.record.avatar_url"
                  :name="row.record.third"
                  :show-member-profile="showMemberProfile"
                  tab="settings"
                  title=""
                />
              </template>
              <template v-else-if="walker.third_type === 'project' || walker.third_type === 'client'">
                <template v-if="walker.third_type === 'client' && row.record.third_id == null">
                  <span class="text-lighter">No client</span>
                </template>
                <template v-else-if="walker.third_type === 'client' && row.record.third_id == 0">
                  <span class="text-lighter">N/A</span>
                </template>
                <template v-else-if="walker.third_type === 'client'">
                  <div class="ellipsis">{{ row.record.third }}</div>
                </template>
                <template v-else-if="walker.third_type === 'project' && row.record.third_id == null">
                  <span class="text-lighter">No project</span>
                </template>
                <template v-else>
                  <div
                    v-tooltip
                    :class="projectClass(row.record.item_type, null, null, row.record.third_id, row.record.third)"
                    :title="projectTitle(row.record.item_type, null, null, row.record.third_id, row.record.third)"
                    @click="onProjectClick(row.record.third_id, row.record.item_type, null, null, row.record.third)"
                  >
                    <ReportTableAvatarIconInitial
                      icon-class="report-avatar-icon"
                      :icons="avatarIconsMap"
                      :icon-key="row.record.item_type"
                      :text="row.record.third"
                    />
                    <div class="ellipsis">{{ row.record.third }}</div>
                  </div>
                </template>
              </template>
              <template v-else-if="walker.third_type === 'date'">
                {{ format_date_label(row.record.third) }}
              </template>
              <template v-else>
                {{ row.record.third }}
              </template>
            </td>
            <td v-else />
          </template>
          <template v-if="memberInfoColumnPosition === 2">
            <td v-if="tableShowMemberInfo">
              <template v-if="row.third_show_data">
                <ReportMemberFields
                  type="info"
                  :data="row.record"
                  :fields="section.infoFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowMemberEmployment">
              <template v-if="row.third_show_data">
                <ReportMemberFields
                  type="employment"
                  :data="row.record"
                  :fields="section.employmentFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowTeams">
              <ReportTeamsField
                v-if="row.third_show_data"
                :teams="row.record.teams"
              />
            </td>
          </template>
          <template v-if="walker.fourth_show">
            <td
              v-if="row.fourth_show_data"
              class="row.fourth_show_data"
            >
              <template v-if="walker.fourth_type === 'user'">
                <MemberPaymentAccountPopup
                  :organization-id="organization.id"
                  :user-id="row.record.fourth_id"
                  :image-url="row.record.avatar_url"
                  :name="row.record.fourth"
                  :show-member-profile="showMemberProfile"
                  tab="settings"
                  title=""
                />
              </template>
              <template v-else-if="walker.fourth_type === 'project' || walker.fourth_type === 'client'">
                <template v-if="walker.fourth_type === 'client' && row.record.fourth == null">
                  <span v-if="row.record.item_type == 'activity'">—</span>
                  <span
                    v-else
                    class="text-lighter"
                  >
                    N/A
                  </span>
                </template>
                <template v-else-if="walker.fourth_type === 'project' && row.record.fourth == null">
                  <span class="text-lighter">No project</span>
                </template>
                <template v-else-if="walker.fourth_type === 'client'">
                  <div class="ellipsis">{{ row.record.fourth }}</div>
                </template>
                <template v-else>
                  <div
                    v-tooltip
                    :class="projectClass(row.record.item_type, null, null, row.record.fourth_id, row.record.fourth)"
                    :title="projectTitle(row.record.item_type, null, null, row.record.fourth_id, row.record.fourth)"
                    @click="onProjectClick(row.record.fourth_id, row.record.item_type, null, null, row.record.fourth)"
                  >
                    <ReportTableAvatarIconInitial
                      icon-class="report-avatar-icon"
                      :icons="avatarIconsMap"
                      :icon-key="row.record.item_type"
                      :text="row.record.fourth"
                    />
                    <div class="ellipsis">{{ row.record.fourth }}</div>
                  </div>
                </template>
              </template>
              <template v-else-if="walker.fourth_type === 'date'">
                {{ format_date_label(row.record.fourth) }}
              </template>
              <template v-else>
                {{ row.record.fourth }}
              </template>
            </td>
            <td v-else />
          </template>
          <template v-if="memberInfoColumnPosition === 3">
            <td v-if="tableShowMemberInfo">
              <template v-if="row.fourth_show_data">
                <ReportMemberFields
                  type="info"
                  :data="row.record"
                  :fields="section.infoFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowMemberEmployment">
              <template v-if="row.fourth_show_data">
                <ReportMemberFields
                  type="employment"
                  :data="row.record"
                  :fields="section.employmentFields"
                  :organization-id="organization.id"
                  :show-all-fields-toggle="false"
                  :walker="walker"
                />
              </template>
            </td>
            <td v-if="tableShowTeams">
              <ReportTeamsField
                v-if="row.fourth_show_data"
                :teams="row.record.teams"
              />
            </td>
          </template>

          <template v-if="tableShowClient">
            <td class="long-text-cell">
              <template v-if="row.record.c_name == null">
                <span v-if="row.record.item_type == 'activity'">—</span>
                <span
                  v-else
                  class="text-lighter"
                >
                  N/A
                </span>
              </template>
              <template v-else>
                <ReportTableAvatarInitial
                  :text="row.record.c_name"
                  class-modifier="avatar avatar-medium"
                />
                {{ row.record.c_name }}
              </template>
            </td>
          </template>
          <template v-if="tableShowTasks">
            <template v-if="!row.aggregate">
              <td
                v-if="hasRemoteTaskId(row.record)"
                class="long-text-cell"
              >
                <TimeAndActivitiesReportTaskPopover :record="row.record">
                  {{ row.record.t_summary }}
                </TimeAndActivitiesReportTaskPopover>
              </td>
              <td
                v-else-if="row.record.t_id != null"
                v-popover:left
                class="long-text-cell"
                :data-content="row.record.t_summary"
              >
                {{ row.record.t_summary }}
              </td>
              <td
                v-else
                class="long-text-cell text-lighter"
              >
                {{ noTaskLabel }}
              </td>
            </template>
            <template v-else>
              <td v-if="row.record.item_type == 'activity'" />
              <td
                v-else
                class="text-lighter"
              >
                N/A
              </td>
            </template>
          </template>
          <template v-if="row.aggregate">
            <td v-if="hasTimeBreakdown">{{ formatDuration(row.aggregate.regular_tracked) }}</td>
            <td v-if="tableShowOvertime">
              <ReportOvertimePopover
                v-if="row.aggregate.overtime"
                :overtime="getOvertimeBreakdown(row)"
                :show-financial="showOvertimeFinancial"
              />
              {{ formatDuration(row.aggregate.overtime) }}
            </td>
            <td v-if="tableShowTimeOffHoliday">
              <i
                v-if="row.record.pto_partial"
                v-tooltip="{ html: true }"
                :data-original-title="timeOffTooltip(row.record)"
                class="hi hi-16 hi-time-off a-tooltip"
                :class="timeOffPartialIconClass(row.record)"
                role="button"
              />
              <span
                v-else-if="row.record.item_type == 'time_off'"
                v-tooltip="{ html: true }"
                :data-original-title="timeOffTooltip(row.record)"
              >
                {{ formatDuration(row.aggregate.time_off_holiday) }}
              </span>
              <span v-else>{{ formatDuration(row.aggregate.time_off_holiday) }}</span>
            </td>
            <td class="duration">
              <template v-if="showTimeoffPartialTotal(row.record)">Not totaled</template>
              <template v-else-if="row.record.pto_partial">—</template>
              <template v-else>{{ $filters.formatDuration(row.aggregate.tracked, 'user', '-', false, true) }}</template>
            </td>
            <td v-if="workBreaksEnabled && tableShowBreakTime">
              {{ formatDuration(row.record.break) }}
            </td>
            <td v-if="tableShowActivity">
              {{ $filters.percentage(row.aggregate.overall, row.aggregate.input_tracked) }}
            </td>
            <td
              v-if="tableShowManualTime"
              data-test="manual-time-aggregate"
            >
              {{ $filters.formatDuration(row.aggregate.manual, 'user', '-', false, true) }}
            </td>
            <td v-if="tableShowIdle">
              {{ $filters.percentage(row.aggregate.idle, row.aggregate.tracked) }}
            </td>
            <td v-if="tableShowIdleTime">
              {{ formatDuration(row.aggregate.idle) }}
            </td>
            <template v-if="tableShowSpentTotal">
              <td v-if="viewPaymentInfo || !tableIsTeam">
                <template v-if="showTimeoffPartialTotal(row.record)">
                  <ReportTableTooltipIcon
                    :message="timeoffPartialTotalTooltip"
                    placement="auto top"
                    type="warning"
                  />
                  Not totaled
                </template>
                <template v-else-if="row.record.pto_partial">—</template>
                <template v-else>{{ $filters.currency(row.aggregate.spent, organization.currency, blankValue) }}</template>
              </td>
              <td v-else />
            </template>
            <td v-if="tableShowSpentRegular">
              {{ formatAggregateSpent('activity', row, row.aggregate, 'spent_regular') }}
            </td>
            <td v-if="tableShowSpentOvertime">
              {{ formatAggregateSpent('activity', row, row.aggregate, 'spent_overtime') }}
            </td>
            <td v-if="tableShowSpentTimeOff">
              <template v-if="!displaySpentValue"></template>
              <template v-else-if="row.record.item_type !== 'time_off'">—</template>
              <template v-else-if="showTimeoffPartialTotal(row.record)">
                <ReportTableTooltipIcon
                  :message="timeoffPartialTotalTooltip"
                  placement="auto top"
                  type="warning"
                />
                Not totaled
              </template>
              <template v-else-if="row.record.pto_partial">—</template>
              <template v-else>{{ $filters.currency(row.aggregate.spent_time_off, organization.currency, blankValue) }}</template>
            </td>
            <td v-if="tableShowSpentHoliday">
              {{ formatAggregateSpent('holiday', row, row.aggregate, 'spent_holiday') }}
            </td>
            <template v-if="tableShowBilledAmount">
              <td v-if="viewPaymentInfo">
                {{ $filters.currency(row.aggregate.billed_amount, organization.currency, blankValue) }}
              </td>
              <td v-else />
            </template>
            <template v-if="tableShowBillableTime">
              <td v-if="viewPaymentInfo">
                {{ formatDuration(row.aggregate.billable_time) }}
              </td>
              <td v-else />
            </template>
            <template v-if="tableShowNonBillableTime">
              <td v-if="viewPaymentInfo">
                {{ formatDuration(row.aggregate.non_billable_time) }}
              </td>
              <td v-else />
            </template>
          </template>
          <template v-else>
            <td v-if="hasTimeBreakdown">{{ formatDuration(row.record.regular_tracked) }}</td>
            <td v-if="tableShowOvertime">
              <ReportOvertimePopover
                v-if="row.record.overtime"
                :overtime="getOvertimeBreakdown(row)"
                :show-financial="showOvertimeFinancial"
              />
              {{ formatDuration(row.record.overtime) }}
            </td>
            <td v-if="tableShowTimeOffHoliday">
              <i
                v-if="row.record.pto_partial"
                v-tooltip="{ html: true }"
                :data-original-title="timeOffTooltip(row.record)"
                class="hi hi-16 hi-time-off a-tooltip"
                :class="timeOffPartialIconClass(row.record)"
                role="button"
              />
              <span
                v-else-if="row.record.item_type == 'time_off'"
                v-tooltip="{ html: true }"
                :data-original-title="timeOffTooltip(row.record)"
              >
                {{ formatDuration(row.record.time_off_holiday) }}
              </span>
              <span v-else>{{ formatDuration(row.record.time_off_holiday) }}</span>
            </td>
            <td class="duration">
              {{ $filters.formatDuration(row.record.tracked, 'user', '-', false, true) }}
            </td>
            <td v-if="workBreaksEnabled && tableShowBreakTime">
              {{ formatDuration(row.record.break) }}
            </td>
            <td v-if="tableShowActivity">
              {{ $filters.percentage(row.record.activity, row.record.input_tracked ? 100 : 0) }}
            </td>
            <td
              v-if="tableShowManualTime"
              data-test="manual-time"
            >
              {{ $filters.formatDuration(row.record.manual, 'user', '-', false, true) }}
            </td>
            <td v-if="tableShowIdle">
              {{ $filters.percentage(row.record.idle, row.record.tracked) }}
            </td>
            <td v-if="tableShowIdleTime">
              {{ formatDuration(row.record.idle) }}
            </td>
            <template v-if="tableShowSpentTotal">
              <td v-if="displaySpentValue">
                <template v-if="showTimeoffPartialTotal(row.record)">
                  <ReportTableTooltipIcon
                    :message="timeoffPartialTotalTooltip"
                    placement="auto top"
                    type="warning"
                  />
                  Not totaled
                </template>
                <template v-else-if="row.record.pto_partial">—</template>
                <template v-else>{{ $filters.currency(row.record.spent, organization.currency, blankValue) }}</template>
              </td>
              <td v-else />
            </template>
            <td v-if="tableShowSpentRegular">
              {{ formatAggregateSpent('activity', row, row.record, 'spent_regular') }}
            </td>
            <td v-if="tableShowSpentOvertime">
              {{ formatAggregateSpent('activity', row, row.record, 'spent_overtime') }}
            </td>
            <td v-if="tableShowSpentTimeOff">
              {{ formatAggregateSpent('time_off', row, row.record, 'spent_time_off') }}
            </td>
            <td v-if="tableShowSpentHoliday">
              {{ formatAggregateSpent('holiday', row, row.record, 'spent_holiday') }}
            </td>
            <template v-if="tableShowBilledAmount">
              <td v-if="viewPaymentInfo">
                {{ $filters.currency(row.record.billed_amount, organization.currency, blankValue) }}
              </td>
              <td v-else />
            </template>
            <template v-if="tableShowBillableTime">
              <td v-if="viewPaymentInfo">
                {{ formatDuration(row.record.billable_time) }}
              </td>
              <td v-else />
            </template>
            <template v-if="tableShowNonBillableTime">
              <td v-if="viewPaymentInfo">
                {{ formatDuration(row.record.non_billable_time) }}
              </td>
              <td v-else />
            </template>
          </template>
          <td
            v-if="tableShowNotes"
            class="long-text-cell"
          >
            <a
              v-if="row.notes.length > 1"
              class="clickable strong"
              @click="openNotesDialog(row.notes, row.notes[0].project_name)"
            >
              {{ row.notes.length }} Notes
            </a>
            <span
              v-else
              v-popover:left
              :data-content="row.notes_summary"
            >
              {{ row.notes_summary }}
            </span>
          </td>
        </tr>
      </ReportVirtualTableBody>
    </ReportVirtualTableBody>
    <NotesDialog
      :show="showNotesDialog"
      :notes="notes"
      :project-name="projectName"
      @close="closeNotesDialog"
    />
  </table>
</template>

<script>
import ReportTableAvatarIconInitial from '~/components/reports/ReportTableAvatarIconInitial.vue';
import NotesDialog from './NotesDialog.vue';
import ReportOvertimePopover from '~/components/reports/ReportOvertimePopover.vue';
import TimeAndActivitiesReportTaskPopover from './TimeAndActivitiesReportTaskPopover.vue';
import MemberPaymentAccountPopup from '~/components/financials/MemberPaymentAccountPopup.vue';
import currency from '~/filters/currency.js';
import { formatDuration } from '~/utils/time_utils';
import { getTaskFlavourLabel } from '~/utils/project_flavour';
import { mapConstants } from '~/app_constants';
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'TimeAndActivitiesReportTable',
  components: {
    ReportTableAvatarIconInitial,
    TimeAndActivitiesReportTaskPopover,
    NotesDialog,
    ReportOvertimePopover,
    MemberPaymentAccountPopup,
  },
  props: {
    tableData: { type: Object, default: () => {} },
  },
  emits: ['change'],
  data() {
    return {
      showNotesDialog: false,
      projectName: null,
      notes: [],
      avatarIconsMap: { holiday: 'hi-gift', time_off: 'hi-time-off' },
      timeoffPartialTotalTooltip: 'Incomplete PTO total because a stop date is outside of the current date filter.',
    };
  },
  computed: {
    spentLabel() {
      return this.tableIsTeam ? 'spent' : 'earned';
    },
    blankValue() {
      if (this.viewPaymentInfo && !this.managePaymentInfo) {
        return '—';
      }

      return ''
    },
    mismatchTooltip() {
      let text = '';

      if (this.tableSumDateRanges && this.tableShowTasks) {
        text =
          'Amounts may not match payments due to rounding when date ranges are being summed or to-dos are included';
      } else if (this.tableShowTasks) {
        text = 'Amounts may not match payments due to rounding when to-dos are included';
      } else if (this.tableSumDateRanges) {
        text = 'Amounts may not match payments due to rounding when date ranges are being summed';
      }

      return text;
    },
    ...mapGetters([
      'employmentFields',
      'infoFields',
      'viewPaymentInfo',
      'managePaymentInfo',
      'memberInfoColumnPosition',
      'showToggledSectionById',
      'tableGroupByTask',
      'tableIsTeam',
      'tableShowAboutMember',
      'tableShowActivity',
      'tableShowBillableTime',
      'tableShowBilledAmount',
      'tableShowBreakTime',
      'tableShowClient',
      'tableShowManualTime',
      'tableShowIdle',
      'tableShowIdleTime',
      'tableShowMemberEmployment',
      'tableShowMemberInfo',
      'tableShowNonBillableTime',
      'tableShowNotes',
      'tableShowSpentTotal',
      'tableShowSpentRegular',
      'tableShowSpentOvertime',
      'tableShowSpentTimeOff',
      'tableShowSpentHoliday',
      'tableShowTasks',
      'tableShowTeams',
      'tableShowOvertime',
      'tableShowTimeOffHoliday',
      'tableSumDateRanges',
      'walker',
      'workBreaksEnabled',
      'reportStartDate',
      'reportEndDate',
      'allProjects',
      'showMemberProfile',
      'virtualTableItemSize',
    ]),
    ...mapState(['organization']),
    ...mapState({
      hasOvertimePolicies: 'has_overtime_policies',
    }),
    ...mapConstants(['ownershipSpentTooltip', 'ownershipBillableTooltip']),
    groupByTask() {
      return this.walker.main_type === 'task';
    },
    groupByUser() {
      return this.walker.main_type === 'user';
    },
    groupByDate() {
      return this.walker.main_type === 'date';
    },
    noTaskLabel() {
      const label = getTaskFlavourLabel(this.organization.project_flavour, true);

      return `No ${label.toLowerCase()}`;
    },
    tableTaskLabel() {
      return getTaskFlavourLabel(this.organization.project_flavour);
    },
    groupNoTaskLabel() {
      const label = this.tableTaskLabel;

      return `No ${label.toLowerCase()}`;
    },
    hasTimeBreakdown() {
      return this.hasOvertimePolicies || this.tableShowTimeOffHoliday;
    },
    displaySpentValue() {
      return this.viewPaymentInfo || !this.tableIsTeam;
    },
    reportStartDateObj() {
      return TimeUtils.parse_internal_date(this.reportStartDate);
    },
    reportEndDateObj() {
      return TimeUtils.parse_internal_date(this.reportEndDate);
    },
    showOvertimeFinancial() {
      return this.viewPaymentInfo && !this.tableSumDateRanges;
    },
    totalRowFirstGroupingSlot() {
      const slots = [
        ['sub', this.walker.sub_show],
        ['third', this.walker.third_show],
        ['fourth', this.walker.fourth_show],
      ];

      return slots.find(([, isVisible]) => isVisible)?.[0] ?? null;
    },
    sections() {
      return this.tableData.sections.map((section) => {
        return {
          ...section,
          show: this.showToggledSectionById(section.main_id),
          itemSize: this.virtualTableItemSize,
          infoFields: this.infoFields,
          employmentFields: this.employmentFields,
        };
      });
    },
  },
  methods: {
    getOvertimeBreakdown({ record: { overtime, pay_rate, pay_rate_multiplier } }) {
      return { overtime, overtimeRate: pay_rate === null ? false : pay_rate * pay_rate_multiplier };
    },
    currency(value, blank = '') {
      return currency(value, this.organization.currency, blank);
    },
    formatDuration(duration, options = {}) {
      const defaultOptions = {
        blank: '—',
        roundSeconds: true,
      };
      const mergedOptions = { ...defaultOptions, ...options };

      return formatDuration(duration, mergedOptions);
    },
    showTotalSpacerForSlot(slotName) {
      const slotVisibility = {
        sub: this.walker.sub_show,
        third: this.walker.third_show,
        fourth: this.walker.fourth_show,
      };

      return slotVisibility[slotName] && this.totalRowFirstGroupingSlot !== slotName;
    },
    format_date_label(value) {
      if (value === 'summed_date') {
        return (
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date)) +
          ' - ' +
          TimeUtils.format_date(TimeUtils.parse_internal_date(this.$store.state.report_query.date_end))
        );
      }

      return TimeUtils.format_date(TimeUtils.parse_internal_date(value));
    },
    formatSpent(value, blank = this.blankValue) {
      if (!this.displaySpentValue) {
        return blank;
      }

      return this.currency(value, blank);
    },
    hasRemoteTaskId(record) {
      return record.t_remote_id && !record.t_remote_id.startsWith('hubstaff_');
    },
    toggleSection(section) {
      this.$store.dispatch('toggleSection', {
        section_id: section.main_id,
        section_count: this.tableData.sections.length,
      });
    },
    openNotesDialog(notes, project) {
      this.notes = notes;
      this.projectName = project;
      this.showNotesDialog = true;
    },
    closeNotesDialog() {
      this.showNotesDialog = false;
    },
    handleAboutChange(payload) {
      this.$emit('change', payload);
    },
    // formatted amount if type match, — otherwise
    formatAggregateSpent(type, row, record, key, blank = this.blankValue) {
      if (!this.displaySpentValue) {
        return blank;
      }
      if (type !== row.record.item_type) {
        return '—';
      }

      return this.currency(record[key], blank);
    },
    parseTimeOffDates(record) {
      record.ptoStartDate ||= TimeUtils.parse_internal_date(record.pto_start);
      record.ptoStopDate ||= TimeUtils.parse_internal_date(record.pto_stop);
    },
    timeOffPartialReasonText(record) {
      this.parseTimeOffDates(record);
      let text = '';

      if (record.pto_stop_outside) {
        text = 'PTO stop date is outside of current date filter';
      } else if (this.groupByDate && record.ptoStartDate < this.reportStartDateObj) {
        text = 'PTO start date is outside of current date filter';
      }

      return text ? `<p>${text}.</p><br>` : text;
    },

    timeOffPartialIconClass(record) {
      this.parseTimeOffDates(record);

      return this.groupByDate || record.ptoStopDate > this.reportEndDateObj ? 'text-warning' : 'text-blue';
    },
    timeOffTooltip(record) {
      this.parseTimeOffDates(record);
      const reason = this.timeOffPartialReasonText(record);

      return `${reason}
        <div>${formatDuration(record.time_off_holiday || record.pto_partial)} paid hours</div>
        <div><span  class="no-wrap">${TimeUtils.format_date(record.ptoStartDate)} -</span>
          <span  class="no-wrap">${TimeUtils.format_date(record.ptoStopDate)}</span></div>`;
    },
    showTimeoffPartialTotal(record) {
      return record.pto_stop_outside;
    },
    sectionSpentTotalTooltip(record) {
      let text = `<p>Regular hours: ${this.currency(record.spent_regular)}</p>`;

      if (this.tableShowSpentOvertime) {
        text += `<p>Overtime: ${this.currency(record.spent_overtime)}</p>`;
      }
      if (this.tableShowSpentTimeOff) {
        text += `<p>PTO: ${this.currency(record.spent_time_off)}</p>`;
      }
      if (this.tableShowSpentHoliday) {
        text += `<p>Holiday: ${this.currency(record.spent_holiday)}</p>`;
      }

      if (this.tableShowSpentTimeOff && this.showTimeoffPartialTotal(record)) {
        text +=
          "<br><p class='text-left'>* Incomplete PTO total because a stop date is outside of the current date filter.</p>";
      }

      return text;
    },
    projectClass(itemType, groupBy, subType, id, name) {
      let classes = '';

      if (itemType === 'holiday' || groupBy === 'client' || subType === 'client') {
        classes = 'd-flex align-items-center';
      } else if (this.isProject(id, name) || itemType === 'time_off') {
        classes = 'hover-underline-project d-inline-flex align-items-center max-w-full';
      } else {
        classes = 'd-flex align-items-center';
      }

      return classes;
    },
    projectTitle(itemType, groupBy, subType, id, name) {
      let title = '';

      if (itemType === 'holiday' || groupBy === 'client' || subType === 'client') {
        title = '';
      } else if (itemType === 'time_off') {
        title = 'View time off requests';
      } else if (this.isProject(id, name)) {
        title = `View project details`;
      } else {
        title = '';
      }

      return title;
    },
    onProjectClick(id, itemType, groupBy, subType, name) {
      if (itemType === 'holiday' || groupBy === 'client' || subType === 'client') { return; }

      const url = new URL(window.location.href);
      let targetUrl = '';

      if (itemType === 'time_off') {
        targetUrl = `${url.origin}/organizations/${this.organization.id}/time_off_requests?filters[all_dates]=on&filters[status]=&filters[time_off_policies][]=${id.match(/\d+/)[0]}`;
      } else if (this.isProject(id, name)) {
        targetUrl = `${url.origin}/projects/${id}`;
      } else {
        return;
      }
      window.open(targetUrl, '_blank').focus();
    },
    isProject(id, name) {
      return this.allProjects.filter((p) => p.type === 'project').map((p) => `${p.id}:${p.name}`).includes(`${id}:${name}`);
    },
  },
};
</script>
